import { useLocation, useSearchParams } from "react-router";
import { QUERY_PARAMS } from "@motain/web-shared-hub/constants";

const KNOWN_PAGE_PARAMS = new Set([QUERY_PARAMS.redirectUrl, "env", "offerId"]);

export const useRedirectUrl = (): string => {
  const [searchParams] = useSearchParams();
  const { search } = useLocation();

  const redirectUrl = searchParams.get(QUERY_PARAMS.redirectUrl) ?? "";

  if (!redirectUrl) {
    return redirectUrl;
  }

  // Check raw query string to see if redirectUrl was properly encoded.
  // URLSearchParams.get() always decodes, so we inspect the raw string to tell
  // the difference between an encoded value ("https%3A%2F%2F...") and a raw
  // URL ("https://...") whose own query params have leaked into the page params.
  const rawMatch = new RegExp(`[?&]${QUERY_PARAMS.redirectUrl}=([^&]*)`).exec(
    search,
  );
  const rawValue = rawMatch?.[1] ?? "";
  const isEncoded = rawValue !== decodeURIComponent(rawValue);

  if (isEncoded) {
    return redirectUrl;
  }

  // Unencoded: any params that don't belong to this page were part of the
  // redirect URL but were misread as top-level page params — collect them back.
  const extraParams = new URLSearchParams();
  searchParams.forEach((value, key) => {
    if (!KNOWN_PAGE_PARAMS.has(key) || key === "offerId") {
      extraParams.append(key, value);
    }
  });

  const extraString = extraParams.toString();
  if (!extraString) {
    return redirectUrl;
  }

  const separator = redirectUrl.includes("?") ? "&" : "?";
  return `${redirectUrl}${separator}${extraString}`;
};
